import { Box, Button, Grid, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { PageHeader } from '@opengov/components-page-header';
import { Download } from '@opengov/react-capital-assets';

import { SectionCard } from '../components/vcm/SectionCard';
import { RESOURCE_CATEGORIES, RESOURCE_STATUSES } from '../lib/vcm/constants';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import { mockGetResources } from '../mocks/vcm-mock-data';

const ALL_COUNTIES = 'All counties';
const ALL_CATEGORIES = 'All';
const ALL_STATUSES = 'All';
const FORMATS = ['CSV', 'Excel (XLSX)'] as const;

/** Escape a CSV cell — wrap in quotes (doubling inner quotes) when it contains a comma, quote, or newline. */
function csvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Build a CSV string from a header row + data rows, then trigger a client-side download. */
function downloadCsv(filename: string, headers: readonly string[], rows: readonly string[][]): void {
  const csv = [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function ResourceExportPage() {
  /* MOCK DATA */
  const resources = useMemo(() => mockGetResources(), []);

  const [countyFilter, setCountyFilter] = useState(ALL_COUNTIES);
  const [categoryFilter, setCategoryFilter] = useState(ALL_CATEGORIES);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [format, setFormat] = useState<string>(FORMATS[0]);

  const counties = useMemo(
    () => [ALL_COUNTIES, ...new Set(resources.map((r) => r.county))],
    [resources],
  );

  const filtered = useMemo(() => {
    let results = resources;
    if (countyFilter !== ALL_COUNTIES) {
      results = results.filter((r) => r.county === countyFilter);
    }
    if (categoryFilter !== ALL_CATEGORIES) {
      results = results.filter((r) => r.category === categoryFilter);
    }
    if (statusFilter !== ALL_STATUSES) {
      results = results.filter((r) => r.status === statusFilter);
    }
    return results;
  }, [resources, countyFilter, categoryFilter, statusFilter]);

  const handleExport = () => {
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(
      `tcr-resource-export-${today}.csv`,
      [
        'Name',
        'Category',
        'County',
        'Service',
        'Organization',
        'Status',
        'Geographic Area',
        'Contact',
        'Eligibility',
      ],
      filtered.map((r) => [
        r.name,
        r.category,
        r.county,
        r.service,
        r.organization,
        r.status,
        r.geographicArea ?? '',
        r.contactInfo ?? '',
        r.eligibilityNotes ?? '',
      ]),
    );
  };

  return (
    <Box
      sx={{
        p: (theme) => theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: (theme) => theme.spacing(2.5),
      }}
    >
      <PageHeader
        title="Resource Export"
        description="Filter the resource inventory and download a CSV for review or reporting."
      />

      <SectionCard title="Export Options">
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="County"
                value={countyFilter}
                onChange={(e) => setCountyFilter(e.target.value)}
              >
                {counties.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Recovery need"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value={ALL_CATEGORIES}>All</MenuItem>
                {RESOURCE_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value={ALL_STATUSES}>All</MenuItem>
                {RESOURCE_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Format"
                value={format}
                onChange={(e) => setFormat(e.target.value)}
              >
                {FORMATS.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
            {filtered.length} matching {filtered.length === 1 ? 'record' : 'records'}
          </Typography>

          <Box>
            <Button
              variant="contained"
              startIcon={<Download />}
              disabled={filtered.length === 0}
              onClick={handleExport}
            >
              Download export
            </Button>
          </Box>
        </Stack>
      </SectionCard>
    </Box>
  );
}
